import { ActionIcon, Center, Flex, Stack, Text } from '@mantine/core';
import { IconEdit as EditIcon, IconRefresh } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { MinStdContext } from '@/module/MinimalStandard/components/MinStdContext';

import { Content, DeleteButton } from '@/core/components';

import { ExternalSourceLink, FileCard, Id, useAppNotification } from '@feature/utils';

import { MinStdItemCardHeader } from './components/card/MinStdItemCardHeader';
import { getMinStdItemFileDownloadUrl } from './model/MinStdApi';
import { useMinStdDeleteIndicatorItem } from './model/MinStdQuery';
import { IndicatorItem } from './model/MinStdTypes';

interface MinStdCriteriaItemCardProps {
  item: IndicatorItem;
  index: number;

  authorId: Id;
  indicatorCode: string;
  legend: string | null;

  onEdit: (item: IndicatorItem) => void;
  onRecalculate?: (item: IndicatorItem) => void;
}

function useItemDelete(authorId: Id, indicatorCode: string) {
  const { mutateAsync, isPending } = useMinStdDeleteIndicatorItem(authorId, indicatorCode);
  const notification = useAppNotification();
  const onDelete = (itemId: Id) => {
    return mutateAsync(itemId).then(notification.success).catch(notification.error);
  };

  return {
    onDelete,
    isDeleting: isPending,
  };
}

export function MinStdIndicatorItemCard({
  authorId,
  indicatorCode,
  item,
  index,
  onEdit,
  legend,
  onRecalculate,
}: MinStdCriteriaItemCardProps) {
  const { onDelete, isDeleting } = useItemDelete(authorId, indicatorCode);
  const { t } = useTranslation();

  return (
    <Content
      flex={1}
      type="full"
    >
      <Flex justify="space-between">
        <Flex flex={1}>
          <Stack
            gap="2px"
            justify="center"
            align="center"
            mr="10px"
          >
            {index != null && <Text fw="500">#{index + 1}</Text>}
            <Text
              c="dimmed"
              fw="500"
              size="xs"
            >
              ({item.points} pct)
            </Text>
            <ActionIcon
              onClick={() => onEdit(item)}
              variant="filled"
              aria-label={t('button.edit')}
            >
              <EditIcon
                style={{ width: '70%', height: '70%' }}
                stroke={1.5}
              />
            </ActionIcon>
            {onRecalculate && (
              <ActionIcon
                onClick={() => onRecalculate(item)}
                variant="filled"
                aria-label={t('button.refresh')}
              >
                <IconRefresh
                  style={{ width: '70%', height: '70%' }}
                  stroke={1.5}
                />
              </ActionIcon>
            )}
          </Stack>
          <Stack
            flex={1}
            gap="4px"
            mr="12px"
          >
            <MinStdItemCardHeader
              item={item}
              legend={legend}
            />
            {item.context && <MinStdContext context={item.context} />}
            <Flex
              flex={1}
              justify="space-between"
            >
              <Stack gap="4px">
                {item.link && (
                  <ExternalSourceLink
                    url={item.link}
                    name={item.link}
                  />
                )}
                <Text
                  c="dimmed"
                  size="sm"
                  fs="italic"
                >
                  {item.note}
                </Text>
              </Stack>
            </Flex>
            {item.file && (
              <FileCard
                file={item.file}
                onDownload={() => window.open(getMinStdItemFileDownloadUrl(authorId, indicatorCode, item.id))}
              />
            )}
          </Stack>
        </Flex>
        <Center>
          <DeleteButton
            size="xs"
            onDelete={() => onDelete(item.id)}
            isDeleting={isDeleting}
          />
        </Center>
      </Flex>
    </Content>
  );
}
